import React from "react";

import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import AuthButton from "@app/analytics/auth/components/AuthButton";
import BackToSignUpOptions from "@app/analytics/auth/components/BackToSignUpOptions";
import TermsSection from "@app/analytics/auth/components/TermsSection";
import PasswordField from "@app/analytics/auth/PasswordField";
import TextField from "@app/analytics/components/TextField";
import useAuth from "@app/common/auth/useAuth";
import useRecaptcha from "@app/common/auth/useRecaptcha";
import type { EmailPassSignUpData } from "@app/common/types";
import * as PolicyUtils from "@app/common/utils/policyUtils";
import Config from "@app/config";
import { yupResolver } from "@app/utils/forms/yupResolver";

import Stack, { Row } from "@rui/Stack";

// Match at least one non-alphanumeric character
const PASSWORD_REGEX = /.*[^a-z0-9]+.*/i;

const emailSignupValidationSchema = yup.object().shape({
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  password: yup.string().required().min(8).matches(PASSWORD_REGEX),
});

interface PasswordContentProps {
  email: string;
  onBack: () => void;
  onSubmit: (data: EmailPassSignUpData) => void;
}

const PasswordContent = ({ email, onBack, onSubmit }: PasswordContentProps) => {
  const { isLoading } = useAuth();

  const { recaptchaRef, getRecaptchaValue } = useRecaptcha();

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<EmailPassSignUpData>({
    resolver: yupResolver(emailSignupValidationSchema),
    defaultValues: { action: "email", email },
  });

  return (
    <Stack
      component="form"
      width="100%"
      onSubmit={handleSubmit(async (data: EmailPassSignUpData) => {
        const recaptchaValue = await getRecaptchaValue();

        onSubmit({
          ...data,
          recaptchaValue,
        });
      })}
    >
      <Row spacing={2} alignItems="flex-start" width="100%">
        <TextField
          data-test-id="auth-first-name-input"
          label="First Name"
          InputProps={{ ...register("firstName") }}
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
        />
        <TextField
          data-test-id="auth-last-name-input"
          label="Last Name"
          InputProps={{ ...register("lastName") }}
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
        />
      </Row>
      <PasswordField
        label="Password"
        helperText="Must contain at least 8 characters, and special symbol"
        error={!!errors.password}
        InputProps={{ ...register("password") }}
      />
      <AuthButton
        data-test-id="auth-button-sign-up"
        sx={{ mt: "30px" }}
        type="submit"
        loading={isLoading}
      >
        Sign up
      </AuthButton>
      <Row variant="center" sx={{ my: "28px" }}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={Config.recaptchaSite}
          badge="inline"
          size="invisible"
        />
      </Row>
      {!PolicyUtils.newPoliciesReleased() && <TermsSection context="signup" />}
      <BackToSignUpOptions onClick={onBack} />
    </Stack>
  );
};

export default PasswordContent;
